import { useEffect, useRef, useState } from 'react';
import { CaretDown, MagnifyingGlass, Check, X } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';

// Searchable single/multi employee picker. NO portal (click-outside close).
// single: value = id|null, onChange(id|null). multi: value = id[], onChange(id[]).
export default function PeopleSelect({ value, onChange, people = [], multi = false, placeholder, testid = 'people', ariaLabel, ariaLabelledBy }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const f = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', f);
    return () => document.removeEventListener('mousedown', f);
  }, []);

  const byId = Object.fromEntries(people.map((p) => [p.id, p]));
  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? people.filter((p) => (p.name || '').toLowerCase().includes(ql) || (p.department_name || '').toLowerCase().includes(ql))
    : people;

  const isSel = (id) => (multi ? (value || []).includes(id) : value === id);
  const toggle = (id) => {
    if (multi) {
      const set = new Set(value || []);
      set.has(id) ? set.delete(id) : set.add(id);
      onChange([...set]);
    } else {
      onChange(id === value ? null : id);
      setOpen(false);
      setQ('');
    }
  };

  const hasVal = multi ? (value && value.length > 0) : !!value;
  const label = multi
    ? (hasVal ? t('planner.people.selected', { n: value.length }) : (placeholder || t('planner.people.none')))
    : (value ? (byId[value]?.name || '') : (placeholder || t('planner.people.none')));

  return (
    <div className="relative mt-1" ref={ref}>
      <button type="button" data-testid={`${testid}-select`} onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} aria-labelledby={ariaLabelledBy}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
        <span className={hasVal ? 'text-zinc-900 truncate' : 'text-zinc-400'}>{label}</span>
        <CaretDown size={14} className="text-zinc-400 shrink-0" />
      </button>

      {multi && hasVal && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`${testid}-chips`}>
          {value.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-xs">
              {byId[id]?.name || id}
              <button type="button" onClick={() => toggle(id)} className="hover:text-indigo-900"><X size={11} weight="bold" /></button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          <div className="flex items-center gap-1.5 border-b border-zinc-200 px-2.5 py-2">
            <MagnifyingGlass size={14} className="text-zinc-400 shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('planner.people.searchPh')}
              data-testid={`${testid}-search`} className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-400" />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {!multi && (
              <button type="button" onClick={() => { onChange(null); setOpen(false); setQ(''); }}
                className="w-full px-3 py-1.5 text-left text-sm text-zinc-500 hover:bg-zinc-50">{t('planner.people.none')}</button>
            )}
            {filtered.map((p) => (
              <button key={p.id} type="button" onClick={() => toggle(p.id)} data-testid={`${testid}-opt-${p.id}`}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-zinc-50 ${isSel(p.id) ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-zinc-700'}`}>
                <span className="truncate">{p.name}<span className="text-[11px] text-zinc-400 ml-1.5">{p.department_name}</span></span>
                {isSel(p.id) && <Check size={14} className="shrink-0" />}
              </button>
            ))}
            {filtered.length === 0 && <p className="px-3 py-2 text-xs text-zinc-400">{t('planner.people.noMatch')}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
